/**
 * Hooks for reading market statistics.
 * 
 * These are thin wrappers around the Zustand market stats store.
 * The actual WebSocket subscription is managed by useMarketStatsSync()
 * which runs once at app root -- see use-market-stats-sync.ts.
 * 
 * Public API is unchanged from the old per-component subscription model:
 * - useMarketStats()  -> { stats, isLoading, isConnected }
 * - useMarketStat(m)  -> MarketStatsInfo | null
 */

import {
  useAllMarketStats,
  useMarketStatFromStore,
  type MarketStatsInfo,
} from "../store/market-stats-store";

// Re-export the type so existing imports don't break
export type { MarketStatsInfo };

/**
 * Get all market stats (backwards compatible).
 * Returns the same { stats, isLoading, isConnected } shape as before.
 * 
 * No WebSocket subscription or setInterval is created -- data comes from the store.
 */
export const useMarketStats = () => {
  return useAllMarketStats();
};

/**
 * Get parsed stats for a single market (backwards compatible).
 * Returns MarketStatsInfo | null with the same shape as before.
 * 
 * No WebSocket subscription or setInterval is created -- data comes from the store.
 */
export const useMarketStat = (market: string): MarketStatsInfo | null => {
  return useMarketStatFromStore(market);
};
